import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as EventoDePonteiro,
} from "react";
import { CampoDeTexto, Esqueleto, cn } from "@base/ui";
import { Button, Modal } from "@base/ui/renke";
import {
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Mail,
  Smartphone,
} from "lucide-react";
import { useClientesGateway } from "../application/clientes.contexto";
import type { AcaoLoginTikTokWeb } from "../infrastructure/clientes.gateway";

type StatusOpcoesTikTok = {
  email: boolean;
  sms: boolean;
  senha: boolean;
  pedeCodigo: boolean;
  contatos?: { sms?: string | undefined; email?: string | undefined } | undefined;
};

/**
 * Opções da tela "Verifique" do TikTok, na ordem dele: Telefone, E-mail,
 * Senha. Se a leitura da tela falhar, mostra as três.
 */
function opcoesDeConfirmacao(opcoes: StatusOpcoesTikTok) {
  const todas = [
    {
      chave: "sms" as const,
      titulo: "Telefone",
      descricao: opcoes.contatos?.sms ?? "Receber código por SMS",
      icone: Smartphone,
      disponivel: opcoes.sms,
    },
    {
      chave: "email" as const,
      titulo: "E-mail",
      descricao: opcoes.contatos?.email ?? "Receber código por e-mail",
      icone: Mail,
      disponivel: opcoes.email,
    },
    {
      chave: "senha" as const,
      titulo: "Senha",
      descricao: "Usar a senha da conta",
      icone: KeyRound,
      disponivel: opcoes.senha,
    },
  ];
  const lidas = todas.filter((opcao) => opcao.disponivel);
  return lidas.length > 0 ? lidas : todas;
}

const ROTULOS_PASSOS_TIKTOK = ["Escanear", "Escolher", "Entrar"] as const;

function PassosLoginTikTok({ atual }: { readonly atual: 1 | 2 | 3 }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Etapas do login">
      {ROTULOS_PASSOS_TIKTOK.map((rotulo, indice) => {
        const numero = indice + 1;
        const feito = numero < atual;
        const agora = numero === atual;
        return (
          <li
            key={rotulo}
            className="flex flex-1 items-center gap-2"
            aria-current={agora ? "step" : undefined}
          >
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium",
                feito || agora
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground",
              )}
              aria-hidden="true"
            >
              {feito ? <Check className="h-3.5 w-3.5" /> : numero}
            </span>
            <span
              className={cn(
                "text-xs",
                agora ? "font-medium text-foreground" : "text-muted-foreground",
              )}
            >
              {rotulo}
            </span>
            {numero < ROTULOS_PASSOS_TIKTOK.length ? (
              <span className="h-px flex-1 bg-border" aria-hidden="true" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

const PASSOS_LOGIN_QR_TIKTOK = [
  "Abra o TikTok no seu celular",
  "Toque no seu perfil e em Adicionar conta ou no menu, escolha Usar código QR",
  "Aponte a câmera para este código e confirme",
] as const;

type FaseLoginQrTikTok = "carregando" | "aguardando" | "expirado" | "falha";

export function ModalLoginQrTikTok({
  aberto,
  onFechar,
  onConectada,
}: {
  readonly aberto: boolean;
  readonly onFechar: () => void;
  readonly onConectada: (username: string) => void;
}) {
  const gateway = useClientesGateway();
  const aoConectarRef = useRef(onConectada);
  aoConectarRef.current = onConectada;
  const [fase, definirFase] = useState<FaseLoginQrTikTok>("carregando");
  const [imagemQr, definirImagemQr] = useState<string | null>(null);
  const [etapa, definirEtapa] = useState<"qr" | "identidade">("qr");
  const [opcoes, definirOpcoes] = useState({
    email: false,
    sms: false,
    senha: false,
    pedeCodigo: false,
  } as StatusOpcoesTikTok);
  const [emCurso, definirEmCurso] = useState<"metodo" | "entrar" | null>(null);
  const [mostrarSenha, definirMostrarSenha] = useState(false);
  const [telaAberta, definirTelaAberta] = useState(false);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [metodo, definirMetodo] = useState<"sms" | "email" | "senha" | null>(null);
  const [valor, definirValor] = useState("");
  const [texto, definirTexto] = useState("");
  const [mostrarTexto, definirMostrarTexto] = useState(false);
  const [enterDepois, definirEnterDepois] = useState(true);
  const [marca, definirMarca] = useState<{ x: number; y: number } | null>(null);
  const inicioDoToque = useRef<{ x: number; y: number } | null>(null);
  const [enviando, definirEnviando] = useState(false);
  const [erroLocal, definirErroLocal] = useState<string | null>(null);
  const [rodada, definirRodada] = useState(0);
  const [vistoAberto, definirVistoAberto] = useState(aberto);
  const desafioIdRef = useRef<string | null>(null);
  const pausado = useRef(false);

  if (aberto !== vistoAberto) {
    definirVistoAberto(aberto);
    if (aberto) {
      definirFase("carregando");
      definirImagemQr(null);
      definirEtapa("qr");
      definirTexto("");
      definirMarca(null);
      definirMetodo(null);
      definirValor("");
      definirTelaAberta(false);
      definirErroLocal(null);
    }
  }

  useEffect(() => {
    if (!aberto) return;

    let ativo = true;
    let intervalo: ReturnType<typeof setInterval> | undefined;
    let consultando = false;

    function pararConsulta() {
      if (intervalo !== undefined) {
        clearInterval(intervalo);
        intervalo = undefined;
      }
    }

    definirFase("carregando");
    definirImagemQr(null);
    definirEtapa("qr");
    definirTexto("");
    definirMarca(null);
    definirMetodo(null);
    definirValor("");
    definirTelaAberta(false);
    definirErroLocal(null);

    function aplicarStatus(status: Awaited<ReturnType<typeof gateway.verificarLoginTikTok>>) {
      if (status.tipo === "aguardando") {
        definirImagemQr(status.imagemQr);
        definirEtapa(status.etapa);
        definirOpcoes(status.opcoes);
        definirErroLocal(null);
        definirFase("aguardando");
        return;
      }
      pararConsulta();
      if (status.tipo === "conectada") {
        aoConectarRef.current(status.username);
        return;
      }
      definirFase("expirado");
    }

    async function consultar(desafioId: string) {
      if (!ativo || consultando || pausado.current) return;
      consultando = true;
      try {
        const status = await gateway.verificarLoginTikTok(desafioId);
        if (!ativo) return;
        aplicarStatus(status);
      } catch (causa) {
        if (!ativo) return;
        definirErroLocal(
          causa instanceof Error && causa.message.trim().length > 0
            ? causa.message
            : "Não foi possível conferir a confirmação no celular.",
        );
      } finally {
        consultando = false;
      }
    }

    async function abrirDesafio() {
      try {
        const iniciado = await gateway.iniciarLoginTikTok();
        if (!ativo) return;
        desafioIdRef.current = iniciado.desafioId;
        definirImagemQr(iniciado.imagemQr);
        definirEtapa("qr");
        definirFase("aguardando");
        intervalo = setInterval(() => {
          void consultar(iniciado.desafioId);
        }, 2500);
      } catch (causa) {
        if (!ativo) return;
        definirErroLocal(
          causa instanceof Error && causa.message.trim().length > 0
            ? causa.message
            : "Não foi possível gerar o QR code.",
        );
        definirFase("falha");
      }
    }

    void abrirDesafio();

    return () => {
      ativo = false;
      pararConsulta();
    };
  }, [aberto, rodada, gateway]);

  async function enviar(acao: AcaoLoginTikTokWeb) {
    const desafioId = desafioIdRef.current;
    if (!desafioId || enviando) return;
    pausado.current = true;
    const tipoDeEnvio = acao.codigo || acao.senha ? "entrar" : acao.metodo ? "metodo" : null;
    definirEmCurso(tipoDeEnvio);
    definirEnviando(true);
    definirErroLocal(null);
    definirAviso(null);
    try {
      const status = await gateway.verificarLoginTikTok(desafioId, acao);
      if (status.tipo === "aguardando") {
        definirImagemQr(status.imagemQr);
        definirEtapa(status.etapa);
        definirOpcoes(status.opcoes);
        if (acao.tela?.tipo === "texto") definirTexto("");
        if (acao.codigo || acao.senha) {
          // Não entrou e não recusou: costuma ser o quebra-cabeça. Mostra a
          // tela e mantém o que a pessoa digitou para tentar de novo.
          definirTelaAberta(true);
          definirAviso(
            "O TikTok ainda não liberou. Confira a tela dele abaixo: se tiver quebra-cabeça, arraste a peça.",
          );
        }
        return;
      }
      if (status.tipo === "conectada") {
        aoConectarRef.current(status.username);
        return;
      }
      definirFase("expirado");
    } catch (causa) {
      definirErroLocal(
        causa instanceof Error && causa.message.trim().length > 0
          ? causa.message
          : "Não consegui enviar essa confirmação para o TikTok.",
      );
      if (acao.metodo && !acao.codigo && !acao.senha) definirMetodo(null);
    } finally {
      definirEnviando(false);
      definirEmCurso(null);
      definirMarca(null);
      pausado.current = false;
    }
  }

  function escolherMetodo(escolhido: "sms" | "email" | "senha") {
    if (enviando) return;
    definirMetodo(escolhido);
    definirValor("");
    definirMostrarSenha(false);
    void enviar({ metodo: escolhido });
  }

  /** Posição do ponteiro na imagem, em fração (0 a 1). */
  function pontoNaImagem(evento: EventoDePonteiro<HTMLImageElement>) {
    const caixa = evento.currentTarget.getBoundingClientRect();
    const limitar = (valor: number) => Math.min(1, Math.max(0, valor));
    return {
      x: limitar((evento.clientX - caixa.left) / caixa.width),
      y: limitar((evento.clientY - caixa.top) / caixa.height),
    };
  }

  function aoSoltarNaImagem(evento: EventoDePonteiro<HTMLImageElement>) {
    const de = inicioDoToque.current;
    inicioDoToque.current = null;
    if (!de || enviando) return;
    const para = pontoNaImagem(evento);
    const caixa = evento.currentTarget.getBoundingClientRect();
    const distancia = Math.hypot(
      (para.x - de.x) * caixa.width,
      (para.y - de.y) * caixa.height,
    );
    definirMarca(para);
    void enviar(
      distancia > 8
        ? { tela: { tipo: "arrasto", de, para } }
        : { tela: { tipo: "clique", ponto: de } },
    );
  }

  function gerarDeNovo() {
    definirFase("carregando");
    definirImagemQr(null);
    definirEtapa("qr");
    definirTexto("");
    definirMarca(null);
    definirMetodo(null);
    definirValor("");
    definirTelaAberta(false);
    definirErroLocal(null);
    definirRodada((atual) => atual + 1);
  }

  const mostrarPassos =
    (fase === "carregando" || fase === "aguardando") && etapa === "qr";

  return (
    <Modal
      aberto={aberto}
      titulo="Entrar no TikTok"
      descricao="Escaneie com o TikTok do celular. A conta pode ser do Google, do Facebook, da Apple ou do telefone: quem confirma é você, no app."
      onFechar={onFechar}
      mostrarX
      travarTopo={false}
      largura={etapa === "identidade" ? 640 : 520}
    >
      <div className="flex flex-col gap-6">
        {erroLocal ? (
          <p className="text-sm leading-relaxed text-destructive" role="alert">
            {erroLocal}
          </p>
        ) : null}

        {fase === "carregando" ? (
          <div
            className="mx-auto flex w-fit flex-col items-center gap-4"
            aria-busy="true"
            aria-live="polite"
          >
            <Esqueleto className="h-[220px] w-[220px] rounded-xl" />
            <p className="text-sm leading-relaxed text-muted-foreground">
              Gerando o QR code…
            </p>
          </div>
        ) : null}

        {fase === "aguardando" && imagemQr && etapa === "qr" ? (
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-xl border border-border bg-background p-4">
              <img
                src={imagemQr}
                alt="QR code para entrar no TikTok"
                width={220}
                height={220}
                className="h-[220px] w-[220px] rounded-lg object-contain"
              />
            </div>
            <p
              className="flex items-center justify-center gap-3 text-center text-sm leading-relaxed text-muted-foreground"
              role="status"
            >
              <span
                className="relative flex h-2.5 w-2.5 shrink-0"
                aria-hidden="true"
              >
                <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-foreground/30 motion-reduce:animate-none" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-foreground/70" />
              </span>
              Esperando você confirmar no celular…
            </p>
          </div>
        ) : null}

        {fase === "aguardando" && imagemQr && etapa === "identidade" ? (
          <div className="flex flex-col gap-5">
            <PassosLoginTikTok atual={metodo && emCurso !== "metodo" ? 3 : 2} />

            <div className="flex flex-col gap-1">
              <p className="text-base font-medium text-foreground">
                Confirme que é você
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                O celular já leu o QR. Escolha como o TikTok vai confirmar. A gente
                faz o resto na tela dele.
              </p>
            </div>

            <div
              className="flex flex-col gap-2"
              role="radiogroup"
              aria-label="Como confirmar no TikTok"
            >
              {opcoesDeConfirmacao(opcoes).map((opcao) => {
                const escolhida = metodo === opcao.chave;
                const abrindo = escolhida && emCurso === "metodo";
                const pronta = escolhida && emCurso !== "metodo";
                const Icone = opcao.icone;
                return (
                  <button
                    key={opcao.chave}
                    type="button"
                    role="radio"
                    aria-checked={escolhida}
                    disabled={enviando && !escolhida}
                    onClick={() => escolherMetodo(opcao.chave)}
                    className={cn(
                      "group flex w-full cursor-pointer items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                      escolhida
                        ? "border-foreground bg-foreground/[0.04]"
                        : "border-border hover:border-foreground/40 hover:bg-foreground/[0.02]",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors",
                        escolhida
                          ? "border-foreground bg-foreground text-background"
                          : "border-border text-foreground",
                      )}
                      aria-hidden="true"
                    >
                      <Icone className="h-[18px] w-[18px]" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm font-medium text-foreground">
                        {opcao.titulo}
                      </span>
                      <span className="truncate text-sm text-muted-foreground">
                        {abrindo ? "Abrindo no TikTok…" : opcao.descricao}
                      </span>
                    </span>
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
                      {abrindo ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground motion-reduce:animate-none" />
                      ) : pronta ? (
                        <Check className="h-4 w-4 text-foreground" />
                      ) : (
                        <span className="h-4 w-4 rounded-full border border-border group-hover:border-foreground/40" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {metodo && emCurso !== "metodo" ? (
              <form
                className="flex flex-col gap-4 rounded-xl border border-border p-4"
                onSubmit={(evento) => {
                  evento.preventDefault();
                  const limpo = metodo === "senha" ? valor : valor.trim();
                  if (!limpo) return;
                  void enviar(
                    metodo === "senha"
                      ? { senha: limpo }
                      : { metodo, codigo: limpo },
                  );
                }}
              >
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {metodo === "senha"
                      ? "Senha do TikTok"
                      : "Código de verificação"}
                  </span>
                  <span className="text-sm leading-relaxed text-muted-foreground">
                    {metodo === "senha"
                      ? "A senha vai direto para o TikTok. Não fica salva aqui."
                      : `O TikTok mandou um código ${
                          metodo === "sms" ? "por SMS" : "por e-mail"
                        }${
                          opcoes.contatos?.[metodo] ? ` para ${opcoes.contatos[metodo]}` : ""
                        }.`}
                  </span>
                  {metodo === "senha" ? (
                    <span className="relative">
                      <input
                        type={mostrarSenha ? "text" : "password"}
                        autoComplete="current-password"
                        autoFocus
                        value={valor}
                        onChange={(evento) => definirValor(evento.target.value)}
                        className="h-11 w-full rounded-lg border border-input bg-background px-3 pr-11 text-sm text-foreground outline-none transition-colors focus:border-foreground focus-visible:ring-2 focus-visible:ring-ring"
                      />
                      <button
                        type="button"
                        onClick={() => definirMostrarSenha((atual) => !atual)}
                        className="absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground"
                        aria-label={mostrarSenha ? "Esconder senha" : "Mostrar senha"}
                      >
                        {mostrarSenha ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </span>
                  ) : (
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={8}
                      placeholder="000000"
                      value={valor}
                      onChange={(evento) =>
                        definirValor(evento.target.value.replace(/\s+/g, ""))
                      }
                      className="h-14 w-full rounded-lg border border-input bg-background px-3 text-center font-mono text-2xl tracking-[0.5em] text-foreground outline-none transition-colors placeholder:text-muted-foreground/40 focus:border-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  )}
                </label>
                <Button
                  type="submit"
                  variante="brand"
                  className="w-full cursor-pointer"
                  carregando={emCurso === "entrar"}
                  disabled={
                    enviando ||
                    (metodo === "senha" ? valor.length === 0 : valor.trim().length < 4)
                  }
                >
                  {emCurso === "entrar" ? "Entrando no TikTok…" : "Entrar"}
                </Button>
                {aviso ? (
                  <p className="text-sm leading-relaxed text-muted-foreground" role="status">
                    {aviso}
                  </p>
                ) : null}
              </form>
            ) : null}

            <details
              className="group/tela flex flex-col"
              open={telaAberta}
              onToggle={(evento) => definirTelaAberta(evento.currentTarget.open)}
            >
              <summary className="cursor-pointer select-none text-sm text-muted-foreground hover:text-foreground">
                {telaAberta
                  ? "Esconder a tela do TikTok"
                  : "Apareceu algo diferente, como um quebra-cabeça? Ver a tela do TikTok"}
              </summary>
              <div className="mt-3 flex flex-col gap-4">
                <div className="relative overflow-hidden rounded-xl border border-border bg-background">
                  <img
                    src={imagemQr}
                    alt="Tela de confirmação do TikTok. Clique para interagir."
                    draggable={false}
                    className={`block h-auto w-full select-none touch-none ${
                      enviando ? "cursor-wait opacity-70" : "cursor-pointer"
                    }`}
                    onPointerDown={(evento) => {
                      if (enviando) return;
                      evento.currentTarget.setPointerCapture(evento.pointerId);
                      inicioDoToque.current = pontoNaImagem(evento);
                    }}
                    onPointerUp={aoSoltarNaImagem}
                    onPointerCancel={() => {
                      inicioDoToque.current = null;
                    }}
                  />
                  {marca ? (
                    <span
                      className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full bg-foreground/40 motion-reduce:animate-none"
                      style={{ left: `${marca.x * 100}%`, top: `${marca.y * 100}%` }}
                      aria-hidden="true"
                    />
                  ) : null}
                </div>
                <form
                  className="flex flex-col gap-3"
                  onSubmit={(evento) => {
                    evento.preventDefault();
                    if (!texto) return;
                    void enviar({ tela: { tipo: "texto", texto, enter: enterDepois } });
                  }}
                >
                  <CampoDeTexto
                    rotulo="Digitar no campo que você clicou"
                    type={mostrarTexto ? "text" : "password"}
                    autoComplete="off"
                    value={texto}
                    onChange={(evento) => definirTexto(evento.target.value)}
                    tamanho="pequeno"
                  />
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={mostrarTexto}
                        onChange={(evento) => definirMostrarTexto(evento.target.checked)}
                      />
                      Mostrar o que digito
                    </label>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={enterDepois}
                        onChange={(evento) => definirEnterDepois(evento.target.checked)}
                      />
                      Apertar Enter no fim
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="submit"
                      variante="secondary"
                      tamanho="sm"
                      className="cursor-pointer"
                      disabled={enviando || texto.length === 0}
                    >
                      {enviando ? "Enviando…" : "Digitar no TikTok"}
                    </Button>
                    <Button
                      type="button"
                      variante="ghost"
                      tamanho="sm"
                      className="cursor-pointer"
                      disabled={enviando}
                      onClick={() => void enviar({ tela: { tipo: "tecla", tecla: "Backspace" } })}
                    >
                      Apagar
                    </Button>
                    <Button
                      type="button"
                      variante="ghost"
                      tamanho="sm"
                      className="cursor-pointer"
                      disabled={enviando}
                      onClick={() => void enviar({ tela: { tipo: "tecla", tecla: "Enter" } })}
                    >
                      Enter
                    </Button>
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Senha e código vão só para a tela do TikTok. Nada fica salvo.
                  </p>
                </form>
              </div>
            </details>
          </div>
        ) : null}

        {(fase === "expirado" || fase === "falha") ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed border-border px-5 py-6 text-center">
            <p className="text-sm font-medium leading-relaxed text-foreground">
              {fase === "expirado" ? "Este QR expirou." : "Não deu para abrir o QR."}
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {fase === "expirado"
                ? "Gere outro código e escaneie de novo no celular."
                : "Tente de novo. Se o celular já está logado no TikTok, o código aparece aqui."}
            </p>
            <Button
              type="button"
              variante="secondary"
              tamanho="sm"
              className="cursor-pointer"
              onClick={gerarDeNovo}
            >
              {fase === "expirado" ? "Gerar novo QR" : "Tentar de novo"}
            </Button>
          </div>
        ) : null}

        {mostrarPassos ? (
          <ol className="flex flex-col gap-3">
            {PASSOS_LOGIN_QR_TIKTOK.map((passo, indice) => (
              <li
                key={passo}
                className="flex items-start gap-3 rounded-xl border border-border px-4 py-3"
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border text-xs font-medium text-foreground"
                  aria-hidden="true"
                >
                  {indice + 1}
                </span>
                <span className="pt-0.5 text-sm leading-relaxed text-foreground">
                  {passo}
                </span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </Modal>
  );
}
